<template>
  <div class="pc-page pc-login-page">
    <pc-status-bar
      class="pc-header"
    />
    <non-resident-modal v-if="showNonResidentModal"></non-resident-modal>
    <div v-else>
      <otp-verification v-if="showOtpVerification" />
      <pc-modal-wrapper v-if="isSmsVerified">
        <pc-modal-container :show-close="false">
          <div class="pc-modal__content">
            <icu-icon class="pc-post-card pc-modal__image"></icu-icon>
            <h3>{{ $t('login-page.get-change-password-link') }}</h3>
            <div class="pc-modal__text">
              {{ $t('login-page.link-was-sent', {email: emailForReset}) }}
            </div>
          </div>
        </pc-modal-container>
      </pc-modal-wrapper>

      <div v-else class="pc-content">
        <pc-modal-container v-if="!isResetPasswordDisplayed" :hide-header="true" :show-close="false">
          <validation-observer v-slot="{invalid}" tag="div" class="login-page-modal pc-modal__form">
            <div class="pc-modal__content">
              <div class="login-page-form">
                <validation-provider v-slot="{errors}" rules="required|email">
                  <pc-text-input
                    v-model="formData.email"
                    :errors="errors"
                    :label="$t('common.email')"
                    :hint="$t('login-page.email-hint')"
                    :debounce-time="0"
                    :force-hint="true"
                    :force-hint-on-focus="true"
                    required
                    @keyup.native.enter="
                      login"
                  />
                </validation-provider>
                <validation-provider v-slot="{errors}" rules="required">
                  <pc-text-input
                    v-model="formData.password"
                    :errors="errors"
                    :label="$t('common.password')"
                    :hint="$t('login-page.password-hint')"
                    :force-hint="true"
                    :force-hint-on-focus="true"
                    :debounce-time="0"
                    required
                    type="password"
                    @keyup.native.enter="login"
                  />
                </validation-provider>
              </div>
            </div>
            <div class="login-page-actions">
              <button class="login-page-actions__reset-password" @click="showResetModal">
                {{ $t('login-page.forgot-password') }}
              </button>
              <div class="login-page-actions-buttons">

                <pc-button variant="main" :disabled="invalid" @click="login">
                  <icu-icon v-if="isLoginWaiting" class="pc-small-loader"></icu-icon>
                  <template v-else>
                    {{ $t('login-page.log-in') }}
                  </template>
                </pc-button>
              </div>
            </div>
          </validation-observer>
        </pc-modal-container>
        <pc-modal-wrapper v-else>
          <pc-modal-container
            @close="closeResetModal"
          >

            <validation-observer v-slot="{invalid}" tag="div" class="pc-modal__form">
              <div class="pc-modal__content">
                <h3 class="text-center">
                  {{ $t('change-password.enter-your-email') }}
                </h3>
                <validation-provider v-slot="{errors}" slim rules="email">
                  <pc-text-input
                    v-model="emailForReset"
                    :errors="[...errors, ...backendErrors.email]"
                    class="pc-modal__input"
                    :label="$t('common.email')"
                    required
                  />
                </validation-provider>
              </div>
              <div class="pc-modal__actions">
                <pc-button
                  variant="main"
                  class="pc-modal__button"
                  :disabled="invalid"
                  @click="changePassword"
                >
                  {{ $t('change-password.change-password') }}
                </pc-button>
              </div>
            </validation-observer>
          </pc-modal-container>
        </pc-modal-wrapper>
      </div>
    </div>
  </div>
</template>

<script>
import ModalWrapper from '@/components/modals/ModalWrapper.vue'
import ModalContainer from '@/components/modals/ModalContainer.vue'
import TextInput from '@/components/inputs/TextInput.vue'
import IcuButton from '@/components/common/IcuButton.vue'
import VerificationModal from '@/components/modals/VerificationModal.vue'
import {ValidationProvider, ValidationObserver} from 'vee-validate'
import CommonModal from '@/components/modals/CommonModal.vue'
import { differenceInSeconds, fromUnixTime } from 'date-fns'
import { constants } from '@/constants.js'
import { api } from '@/api/api.js'
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue'
import NonResidentModal from '@/components/modals/NonResidentModal'
import PcStatusBar from '@/components/views/PrivateCabinet/PcStatusBar'
import PcBlock from '@/components/views/PrivateCabinet/PcBlock'
import PcModalContainer from '@/components/modals/PcModalContainer'
import PcTextInput from '@/components/inputs/PcTextInput'
import PcButton from '@/components/common/PcButton'
import PcModalWrapper from '@/components/modals/PcModalWrapper'
import PcVerificationModal from '@/components/modals/PcVerificationModal'
import IcuIcon from '@/components/common/IcuIcon'
import {v4 as uuidv4} from 'uuid'
import {mapActions, mapGetters} from 'vuex'
import OtpVerification from '@/components/views/PrivateCabinet/OtpVerification'
import redirectMixin from '@/mixins/redirect-mixin'

export default {
  name: 'LoginPage',
  components: {
    OtpVerification,
    IcuIcon,
    PcVerificationModal,
    PcModalWrapper,
    PcButton,
    PcTextInput,
    PcModalContainer,
    PcBlock,
    PcStatusBar,
    NonResidentModal, LanguageSwitcher, CommonModal, VerificationModal, ValidationProvider, ValidationObserver, IcuButton, TextInput, ModalContainer, ModalWrapper },
  mixins: [redirectMixin],
  data() {
    return {
      isResetPasswordDisplayed: false,
      formData: {
        email: '',
        password: '',
        lang: ''
      },
      verificationErrorMessage: '',
      emailForReset: '',
      hiddenPhone: '',
      showNonResidentModal: false,
      isSmsModalDisplayed: false,
      isSmsVerified: false,
      backendErrors: {},
      blockTime: null,
      showResendButton: false,
      liveTime: 0,
      triesLeft: 3,
      verifyId: null,
      isLoginWaiting: false
    }
  },
  computed: {
    ...mapGetters(['showOtpVerification', 'otpConfirmed', 'otpBackendError', 'userInfo', 'settings'])
  },
  codeLifeTimeCountInterval: null,
  codeLifeTimeInSeconds: constants.OTP_CODE_LIFE_IN_SECONDS,
  watch: {
    codeLifeTime() {
      if (!this.codeLifeTime) {
        this.showResendButton = true
      }
    },
    otpBackendError(newVal){
      this.backendErrors = newVal
    },
    isResetPasswordDisplayed(isDisplayed) {
      if (isDisplayed) {
        this.emailForReset = ''
      }
      if (this.$route.query.resetPassword && this.$route.query.email) {
          this.emailForReset = this.$route.query.email
      }
    },
    otpConfirmed(newVal){
      if(newVal === this.verifyId) {
        this.isSmsVerified = true
        this.isResetPasswordDisplayed = false
      }
    },
  },
  beforeMount() {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    this.$store.dispatch('setAppSettings')
    this.$store.dispatch('setUserInfo', {})
    if (this.$route.query.resetPassword) {
      this.isResetPasswordDisplayed = true
    }
  },
  methods: {
    // async verifySms(code) {
    //   try {
    //     await api.auth.checkCode({ code })
    //     this.isSmsModalDisplayed = false
    //     this.isSmsVerified = true
    //   } catch (e) {
    //     const error = e.response.data
    //     if (error._error_code) {
    //       this.processCodeError(error)
    //     } else {
    //       this.verificationErrorMessage = error.message
    //       this.$toast.error(this.$t('notifications.something-went-wrong'))
    //     }
    //   }
    // },

    ...mapActions(['setShowTestModeModal']),

    async login() {
      this.isLoginWaiting = true
      this.formData.lang = this.$i18n.locale
      this.formData.app = 'onboarding'
      try {
        const requestTime = Date.now()
        const accessTTL = Number(localStorage.getItem('access_ttl_limit'))
        const refreshTTL = Number(localStorage.getItem('refresh_ttl_limit'))
        const { data } = await api.auth2.login(this.formData)
        localStorage.setItem('access_token', data.access)
        localStorage.setItem('refresh_token', data.refresh)
        localStorage.setItem('access_ttl', requestTime + accessTTL)
        localStorage.setItem('refresh_ttl', requestTime + refreshTTL)
        localStorage.setItem('scope', data.scope[0])

        await this.$store.dispatch('setUserInfo')
        await this.redirect()

      } catch (e) {
        let errorMessage = this.$t('notifications.something-went-wrong')

        if(e.response?.data._error_code === 'NonResidentNotAllowed') {
          this.showNonResidentModal = true
        } else {
          if (['IncorrectPassword','UserNotFound'].includes(e.response.data._error_code)) {
            errorMessage = this.$t('common.wrong-login-or-password')
          }
          if (e.response?.data?.detail) {
            errorMessage = this.$t(e.response.data?.detail)
          }


          this.$toast.error(errorMessage)
        }
      }
      this.isLoginWaiting = false
    },
    handleBlockTime(blockUntil, serverTime) {
      let seconds = differenceInSeconds(fromUnixTime(blockUntil), fromUnixTime(serverTime))
      this.blockTime = seconds
      const minutes = (Math.floor(seconds / 60)).toString()
      seconds = (seconds % 60).toString()
      this.$toast.error(this.$t('notifications.next-try-will-be-available', { minutes: minutes.padStart(2, '0'), seconds: seconds.padStart(2, '0')}))
    },
    processCodeError(error) {
      switch (error._error_code) {
        case constants.ERROR_CODES.nonResidentNotAllowed:
          this.showNonResidentModal = true
          break
        case constants.ERROR_CODES.wrongCode:
          this.verificationErrorMessage = 'common.wrong-code'
          if (!isNaN(error.block_until)) {
            this.handleBlockTime(error.block_until, error.server_time)
          }
          if (+error.tries >= 3) {
            this.showResendButton = true
          }
          break
        case constants.ERROR_CODES.notFound:
          this.verificationErrorMessage = 'common.code-expired'
          this.showResendButton = true
          break
        case constants.ERROR_CODES.block:
          this.handleBlockTime(error.block_until, error.server_time)
          break
        default:
          this.$toast.error(this.$t('notifications.something-went-wrong'))
      }
    },
    async changePassword() {
      this.backendErrors = {}
      this.verificationErrorMessage = ''
      const id = uuidv4()
      this.verifyId = id
      await this.$store.dispatch('showOtp',{
        id,
        getCodeApi: api.auth.askPassword,
        getCodeApiData:{
          email: this.emailForReset,
          lang: this.$i18n.locale
        },
        confirmCodeApi: api.auth.checkCode,
        showContacts: true,
        hideCountdown: false,
        phoneOnly: true,
      })

    },
    showResetModal(){
      this.isResetPasswordDisplayed = true

      this.$router.replace({ path: this.$route.path, query: { resetPassword: true } })
    },
    closeResetModal(){
      this.isResetPasswordDisplayed = false
      this.$router.replace({ path: this.$route.path, query: {} })
    }
  }
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/views/LoginPage.scss";

.login-page-form {
  padding-top: 16px;
}
.pc-login-page {
  .pc-content {
    height: calc(100vh - 80px);
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.login-page-actions {
  padding-bottom: 16px;
}
</style>
